import { useTranslation } from 'react-i18next';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  OverlayDrawer,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useActiveTheme, useIsMobile } from '@moko/ui';
import ContractorAvailabilityCreate from './ContractorAvailabilityCreate';
import ContractorAvailabilityEdit from './ContractorAvailabilityEdit';
import type {
  ContractorAvailabilityDraft,
  ICreateContractorAvailabilityRuleRequest,
} from '../../services/contractorAvailabilityRuleService';

const DRAWER_MODES = {
  create: {
    title: ['createAvailability', 'Create availability'],
    submit: ['create', 'Create'],
    Content: ContractorAvailabilityCreate,
  },
  edit: {
    title: ['editAvailability', 'Edit availability'],
    submit: ['save', 'Save'],
    Content: ContractorAvailabilityEdit,
  },
} as const;

const CreateContent = DRAWER_MODES.create.Content;
const EditContent = DRAWER_MODES.edit.Content;

export interface ContractorAvailabilityDrawerProps {
  open: boolean;
  mode: keyof typeof DRAWER_MODES;
  contractorId?: number;
  ruleId: number | null;
  draftRule?: ContractorAvailabilityDraft;
  onDraftCreate?: (
    data: Omit<ICreateContractorAvailabilityRuleRequest, 'contractorId'>
  ) => void;
  onDraftUpdate?: (data: ContractorAvailabilityDraft) => void;
  onClose: () => void;
  onSaved: () => void;
}

const ContractorAvailabilityDrawer = ({
  open,
  mode,
  contractorId,
  ruleId,
  draftRule,
  onDraftCreate,
  onDraftUpdate,
  onClose,
  onSaved,
}: ContractorAvailabilityDrawerProps) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const config = DRAWER_MODES[mode];

  return (
    <FluentProvider theme={theme}>
      <OverlayDrawer
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) onClose();
        }}
        position="end"
        size={isMobile ? 'full' : 'medium'}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {t(config.title[0], config.title[1])}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          {open && mode === 'create' && (
            <CreateContent
              contractorId={contractorId}
              submitLabel={t(
                DRAWER_MODES.create.submit[0],
                DRAWER_MODES.create.submit[1]
              )}
              onDraftCreate={onDraftCreate}
              onClose={onClose}
              onSaved={onSaved}
            />
          )}
          {open && mode === 'edit' && ruleId != null && (
            <EditContent
              contractorId={contractorId}
              ruleId={ruleId}
              draftRule={draftRule}
              submitLabel={t(
                DRAWER_MODES.edit.submit[0],
                DRAWER_MODES.edit.submit[1]
              )}
              onDraftUpdate={onDraftUpdate}
              onClose={onClose}
              onSaved={onSaved}
            />
          )}
        </DrawerBody>
      </OverlayDrawer>
    </FluentProvider>
  );
};

export default ContractorAvailabilityDrawer;
